import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

/**
 * Public, no-auth landing page for the email's "Keep tender active" button.
 * The signed token in the URL authenticates the action — no login required.
 */
const KeepTenderActive = () => {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading", message: "Confirming your request..." });
  const [tender, setTender] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const submit = async () => {
      if (!token) {
        setState({ status: "error", message: "This link is invalid." });
        return;
      }
      try {
        const res = await axios.post(
          `${API_BASE_URL}/dashboard/public/tenders/keep-alive/${encodeURIComponent(token)}/`,
          {},
        );
        if (cancelled) return;
        if (res?.data?.success) {
          setTender(res.data.tender || null);
          setState({ status: "success", message: res.data.message || "Your request will stay active." });
        } else {
          setState({ status: "error", message: res?.data?.message || "Could not confirm." });
        }
      } catch (err) {
        if (cancelled) return;
        const code = err?.response?.data?.code;
        const msg = err?.response?.data?.message;
        if (code === "expired") {
          setState({
            status: "expired",
            message: msg || "This link has expired. Please log in to manage your request.",
          });
        } else if (code === "invalid" || err?.response?.status === 400) {
          setState({ status: "error", message: msg || "This link is invalid." });
        } else if (code === "not_found" || err?.response?.status === 404) {
          setState({ status: "error", message: msg || "This request no longer exists." });
        } else if (code === "not_active") {
          setState({ status: "error", message: msg || "This request is no longer active." });
        } else {
          setState({
            status: "error",
            message: "Couldn't reach the server. Please try again or log in to manage your request.",
          });
        }
      }
    };
    submit();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const palette = {
    loading: { bg: "#EFF6FF", border: "#BFDBFE", color: "#1E40AF", icon: "⏳" },
    success: { bg: "#ECFDF5", border: "#A7F3D0", color: "#065F46", icon: "✓" },
    error: { bg: "#FEF2F2", border: "#FECACA", color: "#991B1B", icon: "✕" },
    expired: { bg: "#FFF7ED", border: "#FED7AA", color: "#9A3412", icon: "⌛" },
  }[state.status];

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
      <div
        className="max-w-md w-full bg-white rounded-2xl shadow-sm border p-8 text-center"
        style={{ borderColor: palette.border }}
      >
        <div
          className="mx-auto mb-4 w-14 h-14 rounded-full flex items-center justify-center text-2xl"
          style={{ background: palette.bg, color: palette.color }}
        >
          {palette.icon}
        </div>
        <h1 className="text-xl font-bold text-[#111827] mb-2">
          {state.status === "loading" && "Confirming your request"}
          {state.status === "success" && "Request kept active"}
          {state.status === "expired" && "Link expired"}
          {state.status === "error" && "We couldn't confirm"}
        </h1>
        <p className="text-[15px] text-[#374151] leading-relaxed mb-4">{state.message}</p>
        {tender && (
          <div className="text-sm text-[#6B7280] mb-4">
            <div>
              <span className="font-medium text-[#374151]">Position:</span> {tender.position}
            </div>
            {tender.club_name && (
              <div>
                <span className="font-medium text-[#374151]">Club:</span> {tender.club_name}
              </div>
            )}
          </div>
        )}
        {state.status === "success" && (
          <p className="text-xs text-[#6B7280]">You can safely close this tab.</p>
        )}
        {(state.status === "expired" || state.status === "error") && (
          <Link
            to="/login"
            className="inline-block mt-2 px-5 py-2 rounded-lg bg-[#2283FF] text-white font-medium hover:bg-[#1a6cd4] transition-colors"
          >
            Log in to manage requests
          </Link>
        )}
      </div>
    </div>
  );
};

export default KeepTenderActive;
